/**
 * Config Registry Form Sections Component
 * Collapsible accordion sections for the config form
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ConfigRegistryFormData,
  ConfigRegistryItem,
  UISchemaResponse,
  LookupType,
  LookupValue,
  ConfigRelation,
} from '../types/configRegistryTypes';
import { FormMode } from './ConfigRegistryFormPage';
import { LookupFieldSelect } from './LookupFieldSelect';
import { CreateLookupDialog } from './CreateLookupDialog';
import { LookupTypeHierarchyModal } from './LookupTypeHierarchyModal';
import { ConfigRelationSelect } from './ConfigRelationSelect';
import { AllowedValuesInput } from './AllowedValuesInput';
import { ValidationExpressionInput } from './ValidationExpressionInput';
import { ValidationExpressionGuideModal } from './ValidationExpressionGuideModal';
import { FIELD_TOOLTIPS, getLookupTooltip } from '../constants/fieldTooltips';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface FormSectionsProps {
  formData: ConfigRegistryFormData;
  onFieldChange: (field: keyof ConfigRegistryFormData, value: any) => void;
  uiSchema: UISchemaResponse | null;
  configTypes: string[];
  isReadOnly: boolean;
  mode: FormMode;
  currentConfig: ConfigRegistryItem | null;
  onRefreshUISchema?: () => void;
  onLookupCreated?: (lookupTypeCode: string, newLookup: LookupValue) => void;
}

// Config data types for dropdown - expanded to handle all value types
const CONFIG_DATA_TYPES = [
  { value: 'TEXT', label: 'Text', description: 'Single or multi-line text value' },
  { value: 'INTEGER', label: 'Integer', description: 'Whole number value' },
  { value: 'FLOAT', label: 'Float', description: 'Decimal number value' },
  { value: 'BOOLEAN', label: 'Boolean', description: 'True/False value' },
  { value: 'JSON', label: 'JSON', description: 'Complex JSON object or array' },
  { value: 'DATETIME', label: 'DateTime', description: 'Date and time value' },
  { value: 'COLOR', label: 'Color', description: 'Hex color code (e.g., #D7D7D7)' },
];

// Field tooltips are now imported from '../constants/fieldTooltips'

// Styled accordion common styles - modern card-like appearance
const accordionStyles = {
  mb: 2.5,
  borderRadius: '12px !important',
  border: 'none',
  bgcolor: 'background.paper',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: 0,
    mb: 2.5,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  },
  transition: 'box-shadow 0.2s ease-in-out',
};

const accordionSummaryStyles = (color: string) => ({
  bgcolor: color,
  minHeight: 52,
  px: 2.5,
  '&.Mui-expanded': {
    minHeight: 52,
  },
  '& .MuiAccordionSummary-content': {
    my: 1.5,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: 'rgba(255,255,255,0.9)',
    transition: 'transform 0.2s ease-in-out',
  },
});

// Accordion details with subtle background and padding
const accordionDetailsStyles = {
  pt: 3,
  pb: 3,
  px: 2.5,
  bgcolor: 'grey.50',
  borderTop: '1px solid',
  borderColor: 'divider',
};

// Helper component for field labels with tooltips and optional action
const FieldLabel: React.FC<{
  label: string;
  required?: boolean;
  tooltipKey?: string;
  action?: React.ReactNode;
}> = ({
  label,
  required,
  tooltipKey,
  action
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
    <Typography variant="body2" component="label" sx={{ fontWeight: 500, textAlign: 'left' }}>
      {label} {required && <span style={{ color: '#d32f2f' }}>*</span>}
    </Typography>
    {tooltipKey && FIELD_TOOLTIPS[tooltipKey] && (
      <Tooltip title={FIELD_TOOLTIPS[tooltipKey]} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    )}
    {action}
  </Box>
);

export const ConfigRegistryFormSections: React.FC<FormSectionsProps> = ({
  formData,
  onFieldChange,
  uiSchema,
  configTypes,
  isReadOnly,
  mode,
  currentConfig,
  onRefreshUISchema,
  onLookupCreated,
}) => {
  // Expanded state for accordions - only Basic Information expanded by default
  // Accordion IDs: 'basic', 'classification', 'flags' (DETO Flags)
  const [expanded, setExpanded] = useState<string[]>(['basic']);

  // State for create lookup dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogLookupType, setCreateDialogLookupType] = useState<LookupType | null>(null);
  const [createDialogParentValue, setCreateDialogParentValue] = useState<string | null>(null);

  // State for hierarchy info modal
  const [hierarchyModalOpen, setHierarchyModalOpen] = useState(false);

  // State for validation expression guide modal
  const [validationGuideOpen, setValidationGuideOpen] = useState(false);

  const handleAccordionChange = (panel: string) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(prev =>
      isExpanded ? [...prev, panel] : prev.filter(p => p !== panel)
    );
  };

  // Debug: Log uiSchema and configTypes
  React.useEffect(() => {
    console.log('FormSections - uiSchema:', uiSchema);
    console.log('FormSections - configTypes:', configTypes);
    console.log('FormSections - formData:', formData);
  }, [uiSchema, configTypes, formData]);

  // Get lookup options for a specific type
  const getLookupOptions = useCallback((typeCode: string, parentValue?: string) => {
    if (!uiSchema?.lookupTypes) {
      console.log(`getLookupOptions(${typeCode}): No lookupTypes in uiSchema`);
      return [];
    }
    const lookupType = uiSchema.lookupTypes.find(lt => lt.code === typeCode);
    if (!lookupType) {
      console.log(`getLookupOptions(${typeCode}): LookupType not found. Available:`, uiSchema.lookupTypes.map(lt => lt.code));
      return [];
    }
    // Handle both 'lookups' and 'options' field names
    const options = lookupType.lookups || (lookupType as any).options || [];
    if (!options.length) {
      console.log(`getLookupOptions(${typeCode}): No options found in lookupType:`, lookupType);
      return [];
    }

    if (parentValue && lookupType.dependsOn) {
      return options.filter((l: any) => l.parentValue === parentValue);
    }
    return options;
  }, [uiSchema]);

  // Get lookup type info
  const getLookupType = useCallback((typeCode: string): LookupType | undefined => {
    const lt = uiSchema?.lookupTypes?.find(lt => lt.code === typeCode);
    if (!lt) {
      console.log(`getLookupType(${typeCode}): Not found`);
    }
    return lt;
  }, [uiSchema]);

  // Handle lookup selection
  const handleLookupChange = (typeCode: string, value: string | string[]) => {
    const lookupType = getLookupType(typeCode);
    if (!lookupType) return;

    // Update selected lookups array
    const selectedLookups = formData.selectedLookups || [];
    const newLookups = [...selectedLookups];
    const currentOptions = getLookupOptions(typeCode);
    const currentDocIds = currentOptions.map(o => o.documentId);

    // Remove old selections for this type
    const filtered = newLookups.filter(id => !currentDocIds.includes(id));

    // Add new selections
    const valuesToAdd = Array.isArray(value) ? value : [value];
    const finalLookups = [...filtered, ...valuesToAdd.filter(v => v)];

    onFieldChange('selectedLookups', finalLookups);
  };

  // Handle opening create lookup dialog
  const handleOpenCreateDialog = useCallback((typeCode: string, parentValue?: string) => {
    const lookupType = getLookupType(typeCode);
    if (lookupType) {
      setCreateDialogLookupType(lookupType);
      setCreateDialogParentValue(parentValue || null);
      setCreateDialogOpen(true);
    }
  }, [getLookupType]);

  // Handle lookup created successfully
  const handleLookupCreated = useCallback((newLookup: LookupValue) => {
    if (createDialogLookupType && onLookupCreated) {
      onLookupCreated(createDialogLookupType.code, newLookup);
    }
    // Refresh UI schema to include new lookup
    onRefreshUISchema?.();
    // Also add the new lookup to current selection
    if (createDialogLookupType) {
      const isMulti = createDialogLookupType.allowMultiSelect ||
        (createDialogLookupType as any).fieldType === 'multi-select';
      const currentLookups = formData.selectedLookups || [];
      if (isMulti) {
        onFieldChange('selectedLookups', [...currentLookups, newLookup.documentId]);
      } else {
        // For single select, replace existing selection for this type
        const typeOptions = getLookupOptions(createDialogLookupType.code);
        const typeDocIds = typeOptions.map(o => o.documentId);
        const filtered = currentLookups.filter(id => !typeDocIds.includes(id));
        onFieldChange('selectedLookups', [...filtered, newLookup.documentId]);
      }
    }
  }, [createDialogLookupType, onLookupCreated, onRefreshUISchema, formData.selectedLookups, onFieldChange, getLookupOptions]);

  // Get parent lookup options for create dialog
  const getParentLookupOptionsForDialog = useCallback(() => {
    if (!createDialogLookupType?.dependsOn) return [];
    return getLookupOptions(createDialogLookupType.dependsOn);
  }, [createDialogLookupType, getLookupOptions]);

  // Get current value(s) for a lookup type
  const getLookupValue = (typeCode: string): string | string[] => {
    const lookupType = getLookupType(typeCode);
    const options = getLookupOptions(typeCode);
    const optionDocIds = options.map(o => o.documentId);
    const selectedLookups = formData.selectedLookups || [];
    const selected = selectedLookups.filter(id => optionDocIds.includes(id));

    // Check both allowMultiSelect and fieldType for multi-select
    const isMultiSelect = lookupType?.allowMultiSelect || (lookupType as any)?.fieldType === 'multi-select';
    if (isMultiSelect) {
      return selected; // Always return array for multi-select
    }
    return selected[0] || '';
  };

  // Common text field props with improved disabled/preview styling
  const getTextFieldProps = (field: keyof ConfigRegistryFormData, label: string, required = false) => ({
    fullWidth: true,
    label,
    required,
    value: formData[field] || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onFieldChange(field, e.target.value),
    disabled: isReadOnly,
    size: 'small' as const,
    sx: {
      mb: 2,
      // Improve disabled/preview mode visibility
      '& .MuiInputBase-root.Mui-disabled': {
        bgcolor: 'action.hover',
        '& .MuiInputBase-input': {
          color: 'text.primary',
          WebkitTextFillColor: 'unset',
        },
      },
    },
  });

  // Lookup types to exclude from the UI
  const EXCLUDED_LOOKUP_TYPES = ['COMPLEXITY_LEVEL', 'CONFIG_LEVEL', 'MAX_ENTITY_LEVEL'];

  // Custom ordering: Component, Section, Categorization, then remaining items
  const LOOKUP_TYPE_ORDER = ['COMPONENT', 'SECTION', 'CATEGORIZATION'];

  // Compute which lookup types to show based on parent-child relationships
  const visibleLookupTypes = useMemo(() => {
    if (!uiSchema?.lookupTypes) return [];

    const filtered = uiSchema.lookupTypes.filter((lt) => {
      // Exclude certain lookup types from the UI
      if (EXCLUDED_LOOKUP_TYPES.includes(lt.code)) return false;

      // Always show parent types (those without dependsOn)
      if (!lt.dependsOn) return true;

      // Show child types only if parent has a selection
      const parentValue = getLookupValue(lt.dependsOn);
      if (Array.isArray(parentValue)) {
        return parentValue.length > 0;
      }
      return !!parentValue;
    });

    // Sort according to custom order: Component, Section, Categorization, then remaining
    return filtered.sort((a, b) => {
      const aIndex = LOOKUP_TYPE_ORDER.indexOf(a.code);
      const bIndex = LOOKUP_TYPE_ORDER.indexOf(b.code);

      // If both are in the custom order, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only a is in custom order, it comes first
      if (aIndex !== -1) return -1;

      // If only b is in custom order, it comes first
      if (bIndex !== -1) return 1;

      // For remaining items, maintain alphabetical order by code
      return a.code.localeCompare(b.code);
    });
  }, [uiSchema?.lookupTypes, formData.selectedLookups]);

  // Get parent value for a lookup type (if it has a parent)
  const getParentValueForType = (lt: LookupType): string | undefined => {
    if (!lt.dependsOn) return undefined;
    const parentVal = getLookupValue(lt.dependsOn);
    // For child types, we need the parent's documentId
    if (Array.isArray(parentVal)) {
      return parentVal[0]; // Use first selection for filtering
    }
    return parentVal as string || undefined;
  };

  // Determine which accordions to show based on mode
  const showAllAccordions = mode !== 'create';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Section 1: Basic Information (includes Values & Behavior and Relationships) */}
      <Accordion
        expanded={expanded.includes('basic')}
        onChange={handleAccordionChange('basic')}
        defaultExpanded
        sx={accordionStyles}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={accordionSummaryStyles('primary.main')}>
          <Typography fontWeight={600} color="white">Basic Information</Typography>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsStyles}>
          {/* Config Type and Config Key - Config Type first as requested */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Box>
              <FieldLabel label="Config Type" required tooltipKey="configType" />
              <Autocomplete
                value={formData.configType || ''}
                onChange={(_event, newValue) => {
                  onFieldChange('configType', newValue || '');
                }}
                onInputChange={(_event, newInputValue) => {
                  onFieldChange('configType', newInputValue);
                }}
                options={configTypes}
                disabled={isReadOnly || mode !== 'create'}
                freeSolo
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select or type Config Type..."
                    helperText={mode !== 'create' ? 'Config type cannot be changed after creation' : 'Select from dropdown or type a custom value'}
                  />
                )}
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    bgcolor: 'action.hover',
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                      WebkitTextFillColor: 'unset',
                    },
                  },
                }}
              />
            </Box>
            <Box>
              <FieldLabel label="Config Key" required tooltipKey="configKey" />
              <TextField
                fullWidth
                size="small"
                value={formData.configKey || ''}
                onChange={(e) => onFieldChange('configKey', e.target.value)}
                disabled={isReadOnly || mode !== 'create'}
                placeholder="e.g., enable_feature_x"
                helperText={mode !== 'create' ? 'Config key cannot be changed after creation' : ''}
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    bgcolor: 'action.hover',
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                      WebkitTextFillColor: 'unset',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Display Name and Data Type */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Box>
              <FieldLabel label="Display Name" required tooltipKey="displayName" />
              <TextField
                fullWidth
                size="small"
                value={formData.displayName || ''}
                onChange={(e) => onFieldChange('displayName', e.target.value)}
                disabled={isReadOnly}
                placeholder="Human-readable name"
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    bgcolor: 'action.hover',
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                      WebkitTextFillColor: 'unset',
                    },
                  },
                }}
              />
            </Box>
            <Box>
              <FieldLabel label="Data Type" required tooltipKey="configDataType" />
              <Autocomplete
                value={CONFIG_DATA_TYPES.find(t => t.value === formData.configDataType) || null}
                onChange={(_event, newValue) => onFieldChange('configDataType', newValue?.value || '')}
                options={CONFIG_DATA_TYPES}
                getOptionLabel={(option) => option.label}
                disabled={isReadOnly}
                size="small"
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Data Type..."
                  />
                )}
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    bgcolor: 'action.hover',
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                      WebkitTextFillColor: 'unset',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Documentation */}
          <Box sx={{ mb: 2 }}>
            <FieldLabel label="Documentation" required tooltipKey="configDocumentation" />
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              value={formData.configDocumentation || ''}
              onChange={(e) => onFieldChange('configDocumentation', e.target.value)}
              disabled={isReadOnly}
              placeholder="Describe what this configuration does and how it should be used..."
              sx={{
                '& .MuiInputBase-root.Mui-disabled': {
                  bgcolor: 'action.hover',
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'unset',
                  },
                },
              }}
            />
          </Box>

          {/* Values & Behavior Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'text.primary', textAlign: 'left' }}>
            Values & Behavior
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FieldLabel label="Default Value" tooltipKey="defaultValue" />
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={15}
              size="small"
              value={formData.defaultValue || ''}
              onChange={(e) => onFieldChange('defaultValue', e.target.value)}
              disabled={isReadOnly}
              placeholder="Enter default value (for JSON type, enter valid JSON)"
              sx={{
                fontFamily: formData.configDataType === 'JSON' ? 'monospace' : 'inherit',
                '& .MuiInputBase-root.Mui-disabled': {
                  bgcolor: 'action.hover',
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'unset',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <AllowedValuesInput
              dataType={formData.configDataType}
              value={formData.possibleValues}
              onChange={(value) => onFieldChange('possibleValues', value)}
              disabled={isReadOnly}
              label="Possible Values"
              tooltipKey="possibleValues"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FieldLabel
              label="Validation Expression"
              tooltipKey="validationExpression"
              action={
                <Tooltip title="View expression guide" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setValidationGuideOpen(true)}
                    sx={{
                      ml: 0.5,
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.50' }
                    }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <ValidationExpressionInput
              value={formData.validationRule}
              onChange={(value) => onFieldChange('validationRule', value)}
              disabled={isReadOnly}
            />
          </Box>

          {/* Relationships Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'text.primary', textAlign: 'left' }}>
            Relationships
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left' }}>
            Link this configuration to other configs for hierarchical or reference relationships.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
            {/* Parent Config */}
            <Box>
              <FieldLabel label="Parent Config" tooltipKey="parentConfig" />
              <ConfigRelationSelect
                value={formData.parentConfig}
                onChange={(value) => onFieldChange('parentConfig', value)}
                disabled={isReadOnly}
                helperText="Parent config that this config inherits from (single selection)"
                excludeDocumentId={currentConfig?.documentId}
                multiple={false}
              />
            </Box>

            {/* Related Configs */}
            <Box>
              <FieldLabel label="Related Configs" tooltipKey="relatedConfigs" />
              <ConfigRelationSelect
                value={formData.relatedConfigs}
                onChange={(value) => onFieldChange('relatedConfigs', value)}
                disabled={isReadOnly}
                helperText="Configs that are related but not hierarchical"
                excludeDocumentId={currentConfig?.documentId}
              />
            </Box>

            {/* Duplicate Configs */}
            <Box>
              <FieldLabel label="Duplicate Configs" tooltipKey="duplicateConfigs" />
              <ConfigRelationSelect
                value={formData.duplicateConfigs}
                onChange={(value) => onFieldChange('duplicateConfigs', value)}
                disabled={isReadOnly}
                helperText="Configs that are duplicates of this one"
                excludeDocumentId={currentConfig?.documentId}
              />
            </Box>

            {/* Depends Upon */}
            <Box>
              <FieldLabel label="Depends Upon" tooltipKey="dependsUpon" />
              <ConfigRelationSelect
                value={formData.dependsUpon}
                onChange={(value) => onFieldChange('dependsUpon', value)}
                disabled={isReadOnly}
                helperText="Configs that must be configured before this one"
                excludeDocumentId={currentConfig?.documentId}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Section 2: Classification - Dynamic Lookup Types (Hidden in create mode) */}
      {showAllAccordions && (
        <Accordion
          expanded={expanded.includes('classification')}
          onChange={handleAccordionChange('classification')}
          sx={accordionStyles}
        >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={accordionSummaryStyles('info.main')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography fontWeight={600} color="white">Classification</Typography>
            <Tooltip title="View lookup type relationships">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setHierarchyModalOpen(true);
                }}
                sx={{ ml: 0.5, color: 'white' }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsStyles}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select lookup values to classify this configuration. Some lookups will appear only after their parent is selected.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {/* Dynamically render all visible lookup types */}
            {visibleLookupTypes.map((lookupType) => {
              const parentValue = getParentValueForType(lookupType);
              return (
                <LookupFieldSelect
                  key={lookupType.code}
                  lookupType={lookupType}
                  options={getLookupOptions(lookupType.code, parentValue)}
                  value={getLookupValue(lookupType.code)}
                  onChange={(value) => handleLookupChange(lookupType.code, value)}
                  disabled={isReadOnly}
                  onCreateNew={!isReadOnly ? (typeCode) => handleOpenCreateDialog(typeCode, parentValue) : undefined}
                  parentValue={parentValue}
                  tooltip={getLookupTooltip(lookupType.code)}
                />
              );
            })}
          </Box>
          {visibleLookupTypes.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Loading lookup types...
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      )}

      {/* Section 3: DETO Flags (Hidden in create mode) */}
      {showAllAccordions && (
        <Accordion
        expanded={expanded.includes('flags')}
        onChange={handleAccordionChange('flags')}
        sx={accordionStyles}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={accordionSummaryStyles('warning.main')}>
          <Typography fontWeight={600} color="white">DETO Flags</Typography>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsStyles}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            rowGap: 2.5
          }}>
            {/* Mandatory Field */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '200px auto',
              gap: 1.5,
              alignItems: 'center'
            }}>
              <FieldLabel label="Mandatory Field" tooltipKey="isMandatoryField" />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isMandatoryField}
                    onChange={(e) => onFieldChange('isMandatoryField', e.target.checked)}
                    disabled={isReadOnly}
                    color="primary"
                  />
                }
                label={formData.isMandatoryField ? "Yes" : "No"}
                labelPlacement="end"
                sx={{ m: 0 }}
              />
            </Box>

            {/* Utility Configuration Required */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '200px auto',
              gap: 1.5,
              alignItems: 'center'
            }}>
              <FieldLabel label="Utility Config Required" tooltipKey="utilityConfigurationRequired" />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.utilityConfigurationRequired}
                    onChange={(e) => onFieldChange('utilityConfigurationRequired', e.target.checked)}
                    disabled={isReadOnly}
                    color="primary"
                  />
                }
                label={formData.utilityConfigurationRequired ? "Yes" : "No"}
                labelPlacement="end"
                sx={{ m: 0 }}
              />
            </Box>

            {/* Deprecated */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '200px auto',
              gap: 1.5,
              alignItems: 'center'
            }}>
              <FieldLabel label="Deprecated" tooltipKey="deprecated" />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.deprecated}
                    onChange={(e) => onFieldChange('deprecated', e.target.checked)}
                    disabled={isReadOnly}
                    color="warning"
                  />
                }
                label={formData.deprecated ? "Yes" : "No"}
                labelPlacement="end"
                sx={{ m: 0 }}
              />
            </Box>

            {/* Special Handling Required */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '200px auto',
              gap: 1.5,
              alignItems: 'center'
            }}>
              <FieldLabel label="Special Handling" tooltipKey="specialHandlingRequired" />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.specialHandlingRequired}
                    onChange={(e) => onFieldChange('specialHandlingRequired', e.target.checked)}
                    disabled={isReadOnly}
                    color="secondary"
                  />
                }
                label={formData.specialHandlingRequired ? "Yes" : "No"}
                labelPlacement="end"
                sx={{ m: 0 }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      )}

      {/* Create Lookup Dialog */}
      <CreateLookupDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        lookupType={createDialogLookupType}
        parentLookupOptions={getParentLookupOptionsForDialog()}
        selectedParentId={createDialogParentValue}
        onSuccess={handleLookupCreated}
      />

      {/* Lookup Type Hierarchy Modal */}
      <LookupTypeHierarchyModal
        open={hierarchyModalOpen}
        onClose={() => setHierarchyModalOpen(false)}
        uiSchema={uiSchema}
      />

      {/* Validation Expression Guide Modal */}
      <ValidationExpressionGuideModal
        open={validationGuideOpen}
        onClose={() => setValidationGuideOpen(false)}
      />
    </Box>
  );
};

